import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

export type RunAttribution = { label: string; tone: "admin" | "customer" } | null;

/**
 * Given a run/session with `user_id` + `managed_for_user_ids` and the current
 * user's id, describe who owns it:
 *  - own run → null (no badge)
 *  - an admin ran it for me → "Managed for you"
 *  - someone else's run (admin viewing a customer's) → "Customer run"
 */
export function runAttribution(
	run: { user_id?: string; managed_for_user_ids?: string[] },
	meId?: string,
): RunAttribution {
	if (!run?.user_id) return null;
	if (meId && run.user_id === meId) return null;
	const managedForMe = !!meId && (run.managed_for_user_ids || []).includes(meId);
	if (managedForMe) return { label: "Managed for you", tone: "admin" };
	return { label: "Customer run", tone: "customer" };
}

export function RunAttributionBadge({
	run,
	meId,
	className,
}: {
	run: { user_id?: string; managed_for_user_ids?: string[] };
	meId?: string;
	className?: string;
}) {
	const attr = runAttribution(run, meId);
	if (!attr) return null;
	return (
		<Badge
			variant="outline"
			className={cn(
				"text-[10px] font-medium gap-1 shrink-0",
				attr.tone === "admin"
					? "border-primary/40 text-primary"
					: "text-muted-foreground",
				className,
			)}
			title={attr.tone === "admin" ? "An admin ran this for you" : "Run by another account"}
		>
			{attr.tone === "admin" && <ShieldCheck className="h-3 w-3" />}
			{attr.label}
		</Badge>
	);
}
